import React from 'react';
import { useTranslation } from 'react-i18next';
import SectionCard from '../../components/SectionCard';
import RecordsTable from './RecordsTable';
import TestsTable from './TestsTable';
import type { PersonalRecord, PerformanceTest, SportDiscipline } from './api';

interface DisciplineConfig {
    sport: SportDiscipline;
    labelIt: string;
    label: string;
    colorClass: string;
    shadowClass: string;
    icon: string;
}

interface DisciplineSectionProps {
    config: DisciplineConfig;
    records: PersonalRecord[];
    tests: PerformanceTest[];
    deletingId: string | null;
    onAddRecord: () => void;
    onAddTest: () => void;
    onEditRecord: (rec: PersonalRecord) => void;
    onEditTest: (tst: PerformanceTest) => void;
    onDeleteRecord: (id: string) => void;
    onDeleteTest: (id: string) => void;
}

const DisciplineSection: React.FC<DisciplineSectionProps> = ({
    config,
    records,
    tests,
    deletingId,
    onAddRecord,
    onAddTest,
    onEditRecord,
    onEditTest,
    onDeleteRecord,
    onDeleteTest,
}) => {
    const { t } = useTranslation();
    const sportLabel = t(`personalTest.disciplines.${config.sport}`);
    return (
    <section className="flex flex-col gap-4">

        {/* Discipline heading */}
        <div className="flex items-center gap-3 mb-2">
            <div className={`w-10 h-10 rounded-lg ${config.colorClass} flex items-center justify-center text-white shadow-lg ${config.shadowClass}`}>
                <i className={`fa-solid ${config.icon} text-xl`}></i>
            </div>
            <h2 className="text-2xl font-bold text-slate-800">
                {sportLabel}
            </h2>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <SectionCard
                title={t('personalTest.sectionRecordsOf', { sport: sportLabel })}
                icon="fa-solid fa-medal"
                variant="cyan"
                onAdd={onAddRecord}
            >
                <RecordsTable
                    records={records}
                    deletingId={deletingId}
                    onEdit={onEditRecord}
                    onDelete={onDeleteRecord}
                />
            </SectionCard>

            <SectionCard
                title={t('personalTest.sectionTestsOf', { sport: sportLabel })}
                icon="fa-solid fa-flask"
                variant="cyan"
                onAdd={onAddTest}
            >
                <TestsTable
                    tests={tests}
                    deletingId={deletingId}
                    onEdit={onEditTest}
                    onDelete={onDeleteTest}
                />
            </SectionCard>
        </div>
    </section>
    );
};

export default DisciplineSection;
export type { DisciplineConfig };
